import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import '@/App.css';
import LandingPage from '@/pages/LandingPage';
import Login from '@/pages/Login';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import VerifyEmail from '@/pages/VerifyEmail';
import VerificationPending from '@/pages/VerificationPending';
import Dashboard from '@/pages/Dashboard';
import Bills from '@/pages/Bills';
import Income from '@/pages/Income';
import DebtSnowball from '@/pages/DebtSnowball';
import History from '@/pages/History';
import Goals from '@/pages/Goals';
import LearnInvesting from '@/pages/LearnInvesting';
import Security from '@/pages/Security';
import Contact from '@/pages/Contact';
// BetaAdmin removed - feature deprecated
import Household from '@/pages/Household';
import HouseholdDashboard from '@/pages/HouseholdDashboard';
import HouseholdBills from '@/pages/HouseholdBills';
import HouseholdIncome from '@/pages/HouseholdIncome';
import QuickWins from '@/pages/QuickWins';
import ScheduledExports from '@/pages/ScheduledExports';
import BudgetTemplates from '@/pages/BudgetTemplates';
import EnvelopeChallengeV2 from '@/pages/EnvelopeChallengeV2';
import Badges from '@/pages/Badges';
import Verify2FA from '@/pages/Verify2FA';
import Setup2FA from '@/pages/Setup2FA';
import EmergencyFund from '@/pages/EmergencyFund';
import AdminDashboard from '@/pages/AdminDashboard';
import About from '@/pages/About';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import DataRetention from '@/pages/DataRetention';
import Pricing from '@/pages/Pricing';
import SubscriptionSuccess from '@/pages/SubscriptionSuccess';
import FamilyManagement from '@/pages/FamilyManagement';
import AdminInternalDistribution from '@/pages/AdminInternalDistribution';
import Profile from '@/pages/Profile';
import TaxCalculator1099 from '@/pages/TaxCalculator1099';
import Challenges from '@/pages/Challenges';
import Layout from '@/components/Layout';
import Onboarding from '@/pages/Onboarding';
import IntentPaywall from '@/components/IntentPaywall';
import { ToastProvider } from '@/components/Toast';
import { EnvelopeProvider } from '@/contexts/EnvelopeContext';
import { SubscriptionProvider } from '@/contexts/SubscriptionContext';
import { PremiumActionProvider } from '@/contexts/PremiumActionContext';
import { saveToken, clearToken, getToken } from '@/utils/api';
import { initializeAccessibility } from '@/utils/accessibility';
import { initializeTidio, setTidioUserContext, hideTidio, setupTidioCloseListener } from '@/utils/tidioService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ScrollToTop Component - Resets scroll position on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useLayoutEffect(() => {
    // Scroll to top on every route change
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Hide static landing content when React app loads
if (typeof document !== 'undefined') {
  document.body.classList.add('react-app-loaded');
  const staticLanding = document.getElementById('landing-static');
  if (staticLanding) {
    staticLanding.style.display = 'none';
  }
  
  // Initialize accessibility preferences (reduce motion, high contrast, font size)
  initializeAccessibility();
}

// Auth Callback Component
function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        navigate('/login');
        return;
      }

      try {
        const response = await fetch(`${API}/auth/session`, {
          method: 'POST',
          headers: {
            'X-Session-ID': sessionId,
          },
          credentials: 'include',
        });

        // Handle rate limiting with retry
        if (response.status === 429) {
          console.warn('Rate limited during auth, retrying in 2 seconds...');
          await new Promise(resolve => setTimeout(resolve, 2000));
          hasProcessed.current = false; // Allow retry
          processSession();
          return;
        }

        if (!response.ok) throw new Error('Failed to authenticate');

        const userData = await response.json();
        console.log('Auth response:', userData);
        
        // Check if 2FA verification is required (user has 2FA enabled)
        if (userData.requires_2fa) {
          console.log('Navigating to verify-2fa');
          // Store in BOTH sessionStorage and localStorage for mobile reliability
          const pendingData = {
            user_id: userData.user_id,
            email: userData.email,
            fallback_code: userData.fallback_code
          };
          sessionStorage.setItem('pending_2fa', JSON.stringify(pendingData));
          localStorage.setItem('pending_2fa', JSON.stringify(pendingData));
          // Navigate to 2FA verification page
          navigate('/verify-2fa', { 
            state: pendingData, 
            replace: true 
          });
        } else if (userData.show_2fa_setup) {
          console.log('Navigating to setup-2fa');
          // Save token to localStorage for mobile browsers
          if (userData.session_token) {
            saveToken(userData.session_token);
          }
          // Show 2FA setup prompt for users who haven't been prompted yet
          navigate('/setup-2fa', { state: { user: userData }, replace: true });
        } else {
          console.log('Navigating to dashboard');
          // Save token to localStorage for mobile browsers
          if (userData.session_token) {
            saveToken(userData.session_token);
          }
          // Navigate to dashboard with user data
          navigate('/dashboard', { state: { user: userData }, replace: true });
        }
      } catch (error) {
        console.error('Authentication error:', error);
        navigate('/login');
      }
    };

    processSession();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Authenticating...</p>
      </div>
    </div>
  );
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const authCheckRef = useRef(false);

  useEffect(() => {
    // Prevent duplicate auth checks
    if (authCheckRef.current) return;
    
    // Check if user has pending 2FA - they shouldn't be here
    const pending2FA = sessionStorage.getItem('pending_2fa');
    if (pending2FA) {
      try {
        const pendingData = JSON.parse(pending2FA);
        if (pendingData.user_id && pendingData.email) {
          // User has pending 2FA, redirect to verify
          navigate('/app/verify-2fa', { 
            state: pendingData,
            replace: true 
          });
          return;
        }
      } catch (e) {
        // Invalid data, clear it
        sessionStorage.removeItem('pending_2fa');
      }
    }
    
    // If user data passed from AuthCallback, use it directly
    if (location.state?.user) {
      const userData = location.state.user;
      setUser(userData);
      setIsAuthenticated(true);
      authCheckRef.current = true;
      
      // Pass user context to Tidio (hidden live chat fallback)
      setTidioUserContext(userData);
      
      // Check if this is a new user who needs onboarding
      if (userData.has_completed_onboarding === false && !location.pathname.includes('/onboarding')) {
        navigate('/app/onboarding', { replace: true });
      }
      setCheckingOnboarding(false);
      return;
    }

    // Otherwise verify session with backend
    const checkAuth = async () => {
      // Check if we have a token first
      const token = getToken();
      
      if (!token) {
        // No token at all, redirect to login
        setIsAuthenticated(false);
        setCheckingOnboarding(false);
        navigate('/app/login');
        return;
      }
      
      try {
        const headers = {
          'Authorization': `Bearer ${token}`
        };
        
        const response = await fetch(`${API}/auth/me`, {
          credentials: 'include',
          headers,
        });

        // Handle rate limiting - don't clear token, just wait
        if (response.status === 429) {
          console.warn('Rate limited, waiting before retry...');
          // Don't set authenticated to false on rate limit
          // Just wait and the page will work when limit resets
          setCheckingOnboarding(false);
          return;
        }

        if (!response.ok) {
          throw new Error('Not authenticated');
        }

        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
        authCheckRef.current = true;
        
        // Pass user context to Tidio (hidden live chat fallback)
        setTidioUserContext(userData);
        
        // Check if this is a new user who needs onboarding
        if (userData.has_completed_onboarding === false && !location.pathname.includes('/onboarding')) {
          navigate('/app/onboarding', { replace: true });
        }
        setCheckingOnboarding(false);
      } catch (error) {
        console.error('Auth check failed:', error);
        setIsAuthenticated(false);
        setCheckingOnboarding(false);
        clearToken(); // Clear any stale token
        navigate('/app/login');
      }
    };

    checkAuth();
  }, [navigate, location.state, location.pathname]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return <Layout user={user}>{children}</Layout>;
}

// App Router - Restructured for Landing Page at / and App at /app/*
function AppRouter() {
  const location = useLocation();

  // Check for session_id in URL fragment SYNCHRONOUSLY during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Landing Page at root */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Redirect old login paths to new /app paths */}
      <Route path="/login" element={<Navigate to="/app/login" replace />} />
      <Route path="/signup" element={<Navigate to="/app/signup" replace />} />
      
      {/* Auth routes under /app */}
      <Route path="/app/login" element={<Login />} />
      <Route path="/app/signup" element={<Login />} />
      <Route path="/app/forgot-password" element={<ForgotPassword />} />
      <Route path="/app/reset-password" element={<ResetPassword />} />
      <Route path="/app/verify-email" element={<VerifyEmail />} />
      <Route path="/app/verification-pending" element={<VerificationPending />} />
      <Route path="/app/verify-2fa" element={<Verify2FA />} />
      <Route path="/app/setup-2fa" element={<Setup2FA />} />
      
      {/* Legacy auth routes - redirect to /app */}
      <Route path="/forgot-password" element={<Navigate to="/app/forgot-password" replace />} />
      <Route path="/reset-password" element={<Navigate to="/app/reset-password" replace />} />
      <Route path="/verify-email" element={<Navigate to="/app/verify-email" replace />} />
      <Route path="/verification-pending" element={<Navigate to="/app/verification-pending" replace />} />
      <Route path="/verify-2fa" element={<Navigate to="/app/verify-2fa" replace />} />
      <Route path="/setup-2fa" element={<Navigate to="/app/setup-2fa" replace />} />
      
      {/* Admin Dashboard */}
      <Route path="/admin" element={<AdminDashboard />} />
      
      {/* Public Pages */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/data-retention" element={<DataRetention />} />
      
      {/* Protected App Routes */}
      <Route path="/app" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/app/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/app/bills" element={<ProtectedRoute><Bills /></ProtectedRoute>} />
      <Route path="/app/income" element={<ProtectedRoute><Income /></ProtectedRoute>} />
      <Route path="/app/debt-snowball" element={<ProtectedRoute><DebtSnowball /></ProtectedRoute>} />
      <Route path="/app/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
      <Route path="/app/goals" element={<ProtectedRoute><Goals /></ProtectedRoute>} />
      <Route path="/app/emergency-fund" element={<ProtectedRoute><EmergencyFund /></ProtectedRoute>} />
      <Route path="/app/learn-investing" element={<ProtectedRoute><LearnInvesting /></ProtectedRoute>} />
      <Route path="/app/security" element={<ProtectedRoute><Security /></ProtectedRoute>} />
      <Route path="/app/household" element={<ProtectedRoute><Household /></ProtectedRoute>} />
      <Route path="/app/join-household" element={<ProtectedRoute><Household /></ProtectedRoute>} />
      <Route path="/app/household/:householdId/dashboard" element={<ProtectedRoute><HouseholdDashboard /></ProtectedRoute>} />
      <Route path="/app/household/:householdId/bills" element={<ProtectedRoute><HouseholdBills /></ProtectedRoute>} />
      <Route path="/app/household/:householdId/income" element={<ProtectedRoute><HouseholdIncome /></ProtectedRoute>} />
      <Route path="/app/quick-wins" element={<ProtectedRoute><QuickWins /></ProtectedRoute>} />
      <Route path="/app/scheduled-exports" element={<ProtectedRoute><ScheduledExports /></ProtectedRoute>} />
      <Route path="/app/budget-templates" element={<ProtectedRoute><BudgetTemplates /></ProtectedRoute>} />
      <Route path="/app/envelope-challenge" element={<Navigate to="/app/envelope-v2" replace />} />
      <Route path="/app/envelope-v2" element={<ProtectedRoute><EnvelopeChallengeV2 /></ProtectedRoute>} />
      <Route path="/app/badges" element={<ProtectedRoute><Badges /></ProtectedRoute>} />
      <Route path="/app/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/app/about" element={<ProtectedRoute><About /></ProtectedRoute>} />
      <Route path="/app/contact" element={<ProtectedRoute><Contact /></ProtectedRoute>} />
      {/* Beta Admin route removed */}
      <Route path="/app/pricing" element={<ProtectedRoute><Pricing /></ProtectedRoute>} />
      <Route path="/app/subscription/success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
      <Route path="/app/family" element={<ProtectedRoute><FamilyManagement /></ProtectedRoute>} />
      <Route path="/app/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/app/tax-calculator" element={<ProtectedRoute><TaxCalculator1099 /></ProtectedRoute>} />
      <Route path="/app/challenges" element={<ProtectedRoute><Challenges /></ProtectedRoute>} />
      <Route path="/challenges/join/:inviteCode" element={<ProtectedRoute><Challenges /></ProtectedRoute>} />
      
      {/* Admin-only routes */}
      <Route path="/admin/internal-distribution" element={<ProtectedRoute><AdminInternalDistribution /></ProtectedRoute>} />
      
      {/* Legacy routes - redirect to /app versions */}
      <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
      <Route path="/bills" element={<Navigate to="/app/bills" replace />} />
      <Route path="/income" element={<Navigate to="/app/income" replace />} />
      <Route path="/debt-snowball" element={<Navigate to="/app/debt-snowball" replace />} />
      <Route path="/history" element={<Navigate to="/app/history" replace />} />
      <Route path="/goals" element={<Navigate to="/app/goals" replace />} />
      <Route path="/emergency-fund" element={<Navigate to="/app/emergency-fund" replace />} />
      <Route path="/learn-investing" element={<Navigate to="/app/learn-investing" replace />} />
      <Route path="/security" element={<Navigate to="/app/security" replace />} />
      <Route path="/household" element={<Navigate to="/app/household" replace />} />
      <Route path="/join-household" element={<Navigate to="/app/join-household" replace />} />
      <Route path="/quick-wins" element={<Navigate to="/app/quick-wins" replace />} />
      <Route path="/scheduled-exports" element={<Navigate to="/app/scheduled-exports" replace />} />
      <Route path="/budget-templates" element={<Navigate to="/app/budget-templates" replace />} />
      <Route path="/envelope-challenge" element={<Navigate to="/app/envelope-challenge" replace />} />
      <Route path="/badges" element={<Navigate to="/app/badges" replace />} />
      <Route path="/onboarding" element={<Navigate to="/app/onboarding" replace />} />
    </Routes>
  );
}

function App() {
  // Initialize Tidio on app mount (hidden by default)
  // Setup close listener so Tidio hides when user closes it (prevents blocking Guapo)
  useEffect(() => {
    initializeTidio();
    const cleanup = setupTidioCloseListener();
    return cleanup;
  }, []);

  return (
    <ToastProvider>
      <SubscriptionProvider>
        <EnvelopeProvider>
          <div className="App">
            <BrowserRouter>
              <ScrollToTop />
              <PremiumActionProvider>
                <AppRouter />
                <IntentPaywall />
              </PremiumActionProvider>
            </BrowserRouter>
          </div>
        </EnvelopeProvider>
      </SubscriptionProvider>
    </ToastProvider>
  );
}

export default App;